// Public serializable types for the Echo Engine sticker gameplay boundary.
//
// These types form the stable public contract that gameplay code depends on.
// They intentionally expose NOTHING about how scanning is implemented (no
// camera, no compute pipelines, no backend). Sticker codes are opaque decimal
// strings; save data is free-form JSON.

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export interface JsonObject {
  [key: string]: JsonValue;
}

/**
 * An opaque sticker identifier. Always a decimal string. Gameplay code must
 * treat this as an opaque token and must not attempt to decode structure from
 * it — the mapping from physical sticker to code is a production concern.
 */
export type StickerCode = string;

/**
 * A project tag. Shared contract with the first-party authoring tool
 * (Echoprint): the stable `id` is identity, `name` is display-only. Every
 * sticker code belongs to a batch/project that carries exactly one tag; app
 * facing recognition resolves the reference and returns the complete `{id,name}`.
 * Names are trimmed and unique per project case-insensitively.
 */
export interface ProjectTag {
  /** Stable identity. Never shown to players as a label; use `name`. */
  id: string;
  /** Human-readable, trimmed display label. */
  name: string;
}

/**
 * The default tag for codes with no explicit tag. Legacy data without a tag
 * normalizes non-destructively to this value at the service boundary.
 */
export const UNASSIGNED_TAG: ProjectTag = { id: 'unassigned', name: 'Unassigned' };

/**
 * Activation/use status of a sticker code, mirroring the first-party server's
 * response contract (`not_activated | activated | used`). A code that has opened
 * a session and owns a save game is `used`.
 */
export type StickerCodeStatus = 'not_activated' | 'activated' | 'used';

/** A sticker and its resolved project tag within a linked collection. */
export interface StickerIdentity {
  readonly code: StickerCode;
  readonly tag: ProjectTag;
}

/**
 * Application-facing state for the two-scan collection workflow.
 *
 * `new_robot` asks the application to choose between starting a new collection
 * and adding the robot to an existing one. `awaiting_existing_robot` asks for a
 * second scan. `robot_linked` is emitted once after that link succeeds.
 */
export type StickerCollectionState =
  | 'ready'
  | 'new_robot'
  | 'awaiting_existing_robot'
  | 'robot_linked';

export interface StickerCollection {
  readonly state: StickerCollectionState;
  readonly members: readonly StickerIdentity[];
  readonly pendingRobot?: StickerIdentity;
  readonly addedRobot?: StickerIdentity;
}

/**
 * A live, anonymous gameplay session linked to a scanned sticker. Contains a
 * usable save game. Everything here is serializable and safe to hand to
 * gameplay code. No camera, Firebase, activation, or storage internals leak
 * through this surface.
 */
export interface StickerSession<T extends JsonObject = JsonObject> {
  /** Stable id for this session for the lifetime of the browser session. */
  readonly sessionId: string;
  /** The sticker code that opened this session. */
  readonly code: StickerCode;
  /**
   * Persistent id of the save game backing this session (mirrors the server's
   * `saveGameId`). Stable across scans of the same/linked codes.
   */
  readonly saveGameId: string;
  /** Activation/use status of the code, mirroring server response semantics. */
  readonly status: StickerCodeStatus;
  /**
   * Project tag associated with this code's batch/project. Always present and
   * fully resolved (`{id,name}`); untagged/legacy codes resolve to
   * {@link UNASSIGNED_TAG}.
   */
  readonly tag: ProjectTag;
  /**
   * Other stickers linked to the same save, including each sticker's resolved
   * project tag so gameplay never has to decode or look up an opaque code.
   */
  readonly linkedCodes: readonly StickerIdentity[];
  /** Deep-cloned free-form save data. Mutating this does not affect the session. */
  readonly save: T;
  /** Engine-owned linked-collection state and resolved member identities. */
  readonly collection: StickerCollection;
}

export type StickerErrorCode =
  | 'unsupported'
  | 'scan_failed'
  | 'not_scanning'
  | 'invalid_save'
  | 'cancelled'
  | 'storage_unavailable'
  | 'no_next_code'
  | 'collection_conflict'
  | 'same_sticker'
  | 'internal';

/** Typed error thrown/returned across the sticker boundary. */
export class StickerError extends Error {
  readonly code: StickerErrorCode;
  constructor(code: StickerErrorCode, message: string) {
    super(message);
    this.name = 'StickerError';
    this.code = code;
  }
}

/** Options for a scan attempt. */
export interface ScanOptions {
  /** Abort the scan (maps to a `cancelled` result). */
  signal?: AbortSignal;
  /** Ask the adapter to open a specific sticker instead of cycling fixtures. */
  preferredCode?: StickerCode;
}

/** Discriminated result of a scan attempt. Never throws for expected outcomes. */
export type ScanResult<T extends JsonObject = JsonObject> =
  | { status: 'scanned'; session: StickerSession<T> }
  | { status: 'cancelled' }
  | { status: 'error'; error: StickerError };
